<template>
    <evp-layout>
        <navigation-header
            :return-url="route('profile')"
            :title="$t('global.PrivacyCenterTitle')"
        />

        <div class="content pt-2">
            <a 
                class="group flex place-items-center bg-surface py-3 px-1 mx-0.5 mt-1 mb-5 shadrow-primary-sm hover:bg-primary-dark rounded-md" 
                :href="route('data-processing-rules')"
            >
                <div class="flex flex-col place-items-center pl-2 pr-5 group-hover:text-white">
                    <svg
                        class="w-8 h-8 m-1.5 document-icon-other group-hover:fill-white"
                        fill="#00a8d7"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                    >
                        <path d="M6 2c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6H6zm7 7V3.5L18.5 9H13z"></path>
                    </svg>
                </div>
                <div class="text-primary group-hover:text-white underline text-lg">
                    {{ $t('global.PrivacyPolicyTitle') }}
                </div>
            </a>
            
            <template v-if="cookies.length">
                <p class="pl-1.5 pb-1.5">
                    {{ $t('profile.CookieListLabel') }}
                </p>

                <div class="bg-surface rounded-md">
                    <div v-for="(cookie, index) in cookies" class="flex shadow-sm m-0.5" :key="index">

                        <div class="w-6 h-6 m-2.5 shrink-0">
                            <svg
                                v-if="cookie['iconName'] == 'SettingsIcon'"
                                class="fill-medium-gray/50"
                                xmlns:xlink="http://www.w3.org/1999/xlink"
                                version="1.1"
                                baseProfile="full"
                                viewBox="0 0 75 75"
                                xml:space="preserve"
                            ><path d="M 38,23.5C 38.8643,23.5 39.7109,23.5756 40.5337,23.7206L 42.6275,18.5381L 48.1901,20.787L 46.0964,25.9692C 47.6473,27.0149 48.9851,28.3527 50.0308,29.9036L 55.213,27.8099L 57.4619,33.3725L 52.2794,35.4664C 52.4244,36.2891 52.5,37.1357 52.5,38C 52.5,38.8643 52.4244,39.7109 52.2794,40.5337L 57.4619,42.6275L 55.213,48.1901L 50.0308,46.0964C 49.0795,47.5073 47.8865,48.7418 46.5112,49.7405L 48.7844,54.8462L 43.3041,57.2891L 41.0307,52.1828C 40.0533,52.3906 39.0394,52.5 38,52.5C 37.1357,52.5 36.2891,52.4244 35.4664,52.2794L 33.3725,57.462L 27.8099,55.213L 29.9036,50.0309C 28.3527,48.9851 27.0149,47.6473 25.9691,46.0964L 20.787,48.1901L 18.538,42.6275L 23.7206,40.5336C 23.5756,39.7109 23.5,38.8643 23.5,38C 23.5,37.1357 23.5756,36.2891 23.7206,35.4664L 18.538,33.3725L 20.787,27.8099L 25.9691,29.9036C 26.9205,28.4927 28.1135,27.2582 29.4889,26.2594L 27.2157,21.1537L 32.6959,18.7109L 34.9694,23.8172C 35.9468,23.6094 36.9606,23.5 38,23.5 Z M 38,28C 32.4771,28 28,32.4772 28,38C 28,43.5229 32.4771,48 38,48C 43.5228,48 48,43.5229 48,38C 48,32.4772 43.5228,28 38,28 Z"></path></svg>
                            <svg
                                v-else-if="cookie['iconName'] == 'GAIcon'"
                                class="fill-medium-gray/50"
                                xmlns:xlink="http://www.w3.org/1999/xlink"
                                version="1.1"
                                baseProfile="full"
                                viewBox="0 0 24 24"
                                xml:space="preserve"
                            ><path d="M19,3A2,2 0 0,1 21,5V19A2,2 0 0,1 19,21H17L15,21V9L15,5A2,2 0 0,1 17,3H19M5,21A2,2 0 0,1 3,19V17A2,2 0 0,1 5,15H9V11A2,2 0 0,1 11,9H14.5V21H5Z "></path></svg>
                        </div>

                        <div class="py-2.5">
                            <p class="font-black">
                                {{ cookie['name'] }}
                            </p>
                            <p>
                                {{ cookie['description'] }}
                            </p>
                        </div>

                        <button
                            v-if="!cookie['isRequired']"
                            @click="deleteCookie(cookie['name'])"
                            type="button"
                            class="button-secondary group w-11 h-11 p-2.5 m-0.5 ml-auto shrink-0 border-none shadow-none hover:bg-alert-red"
                        >
                            <svg class="group-hover:!fill-white !fill-medium-gray/50" viewBox="0 0 24 24"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM8 9h8v10H8V9zm7.5-5l-1-1h-5l-1 1H5v2h14V4z" /><path fill="none" d="M0 0h24v24H0V0z" /></svg>
                        </button>
                    </div>
                </div>
            </template>

            <template v-if="!cookieConsent">
                <div class="py-1 px-4 mb-2 mt-11 bg-alert-orange/10 border-2 border-alert-orange/50">
                    {{ $t('profile.NoGAConsentText') }}
                </div>

                <button @click="acceptAllCookies" type="button" class="button-secondary h-11 flex place-items-center w-full hover:!border-primary-dark hover:bg-primary-dark hover:!text-white">
                    <svg
                        class="w-6 h-6 m-2.5"
                        xmlns:xlink="http://www.w3.org/1999/xlink"
                        version="1.1"
                        baseProfile="full"
                        viewBox="0 0 75 75"
                        xml:space="preserve"
                    ><path d="M 35,19L 41,19L 41,35L 57,35L 57,41L 41,41L 41,57L 35,57L 35,41L 19,41L 19,35L 35,35L 35,19 Z  "></path></svg>
                    {{ $t('profile.ReconsentToGAButton') }}
                </button>
            </template>
        </div> 
    </evp-layout>
</template>
<script setup>
    import EvpLayout from '@/components/EvpLayout.vue'
    import { route } from 'ziggy'
    import NavigationHeader from '@/components/NavigationHeader.vue'

    const cookies = window.privacyData.cookies
    const cookieConsent = window.privacyData.cookieConsent

    function acceptAllCookies() {
        window.acceptAllCookies()
    }

    function deleteCookie(name) {
        window.deleteCookie(name)
    }
</script>